<template lang="html">
  <div
    :class="[
      'search-suggests flex flex-col gap-5',
      isNoResults ? 'h-full min-h-full flex-1' : '',
      showTwoColumns
        ? 'sm:grid sm:grid-cols-[minmax(11rem,0.75fr)_minmax(0,1.85fr)] sm:items-start sm:gap-0'
        : '',
    ]"
  >
    <!-- Left (desktop): matching categories -->
    <div
      v-if="filteredCategories.length"
      :class="[
        'search-suggests__categories min-w-0',
        showTwoColumns
          ? 'sm:me-5 sm:border-e sm:border-default sm:pe-5'
          : '',
      ]"
    >
      <p class="mb-2 text-xs font-semibold tracking-wide text-muted uppercase">
        Категории
      </p>
      <ul class="flex flex-col gap-0.5">
        <li
          v-for="category in filteredCategories"
          :key="category.id"
        >
          <UButton
            variant="ghost"
            color="neutral"
            class="w-full justify-start px-2 py-2 text-left font-normal"
            @click="goToCategory(category)"
          >
            {{ category.displayName }}
          </UButton>
        </li>
      </ul>
    </div>

    <!-- Right: product cards (minimal) / empty -->
    <div
      :class="[
        'search-suggests__products flex min-w-0 flex-col gap-3',
        isNoResults ? 'h-full min-h-full flex-1' : '',
      ]"
    >
      <div
        v-if="productsPending"
        class="search-suggests__products-loading space-y-2 py-1"
        aria-busy="true"
        aria-live="polite"
      >
        <USkeleton class="h-3 w-16" />
        <div class="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-3">
          <USkeleton
            v-for="n in SUGGEST_PRODUCT_LIMIT"
            :key="`suggest-skel-${n}`"
            class="aspect-square w-full"
          />
        </div>
      </div>

      <template v-else-if="filteredProducts.length">
        <p class="text-xs font-semibold tracking-wide text-muted uppercase">
          Товары
        </p>
        <div class="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3">
          <ProductCard
            v-for="(product, index) in filteredProducts"
            :key="product.id"
            mode="minimal"
            :value="product"
            :eager="index < 3"
            @click="onProductSelect"
          />
        </div>
      </template>

      <div
        v-else-if="isNoResults"
        class="search-suggests__empty flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-4 py-10 text-center"
        role="status"
        aria-live="polite"
      >
        <UIcon
          name="i-lucide-search-x"
          class="mb-1 size-10 text-muted"
        />
        <p class="text-base font-semibold text-highlighted">
          По запросу «{{ props.query }}» ничего не найдено
        </p>
        <p class="max-w-sm text-sm text-muted">
          Попробуйте изменить запрос или откройте полную выдачу.
        </p>
      </div>

      <div
        v-if="props.query"
        :class="[
          'search-suggests__see-all flex shrink-0 justify-end border-t border-default pt-3',
          isNoResults ? 'mt-auto' : 'mt-1',
        ]"
      >
        <UButton
          variant="link"
          color="primary"
          trailing-icon="i-lucide-arrow-right"
          class="font-semibold"
          @click="goToSearch"
        >
          Найти всё
        </UButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ProductListItemDto } from '@repo/api/web/models'
import {
  EntityRef,
  type ProductsListGetRequestQuery,
} from '@repo/api/web/models'

import ProductCard from '#Products/components/Card/ProductCard.vue'
import type { CategoryDto } from '#Products/models/Pim/CategoryDto'

const SUGGEST_PRODUCT_LIMIT = 12

const props = defineProps<{
  query: string
}>()

const emit = defineEmits<{
  select: []
}>()

const { service, routing } = useProducts()
const { byIds } = await useCategories()

const filteredCategories = computed(() => {
  if (!props.query || productsPending.value) return []

  const query = props.query.toLowerCase()
  const found = Object.values(byIds.value)
    .filter((category: CategoryDto) =>
      category.name.toLowerCase().includes(query),
    )
    .slice(0, 6)
    .map(category =>
      EntityRef.createLazy({ id: category.id, displayName: category.name }),
    )
  const categoriesMap = new Map<number, EntityRef>(
    found.map(category => [category.id, category]),
  )

  for (const category of iterateProductsCategories()) {
    if (categoriesMap.size >= 6) break

    if (category && !categoriesMap.has(category.id)) {
      categoriesMap.set(category.id, category)
    }
  }

  return Array.from(categoriesMap.values())
})

function* iterateProductsCategories() {
  if (!productsData.value?.items) yield undefined

  for (const product of productsData.value?.items || []) {
    for (const category of product.categories || []) {
      yield category
    }
  }
}

const { data: productsData, pending: productsPending } = await useAsyncData(
  `search-suggests-${props.query}`,
  () =>
    service.list({
      Query: props.query,
      Limit: SUGGEST_PRODUCT_LIMIT,
    } as ProductsListGetRequestQuery),
  {
    watch: [() => props.query],
    server: false,
  },
)

const filteredProducts = computed(() => {
  return ProductListItemDto.createFromCollection(
    productsData.value?.items ?? [],
  )
})

const showTwoColumns = computed(
  () =>
    filteredCategories.value.length > 0
    && (filteredProducts.value.length > 0 || productsPending.value),
)

/** No categories and no products after load — blank popover without this. */
const isNoResults = computed(
  () =>
    Boolean(props.query)
    && !productsPending.value
    && filteredProducts.value.length === 0
    && filteredCategories.value.length === 0,
)

const goToCategory = (category: EntityRef) => {
  emit('select')
  navigateTo(
    routing.getCatalog({
      id: category.id,
      displayName: category.displayName,
    } as EntityRef).route,
  )
}

const onProductSelect = () => {
  emit('select')
}

const goToSearch = () => {
  emit('select')
  navigateTo(routing.getSearch(props.query).route)
}
</script>
